import React, { useCallback, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Form, Popconfirm, Space, notification } from 'antd';
import DF from 'DisplayFramework';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import SaveButton from 'components/SaveButtonAction';
import DeleteButton from 'components/DeleteButton';
import {
  addParDocSupplier,
  getParDocSupplier,
  removeParDocSupplier,
  updateParDocSupplier,
  ParDocSupplierItem,
} from 'services/Document';
import FormInput from 'components/FormInput';
import NewButton from 'components/NewButton';

type RowItem = ParDocSupplierItem & {
  isNew?: boolean;
};

const ParDocSuppInvoice: React.FC = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState<RowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const lastFetchId = useRef(0);

  const fetchData = useCallback(async (criteria: Partial<ParDocSupplierItem>) => {
    const fetchId = ++lastFetchId.current;
    try {
      setLoading(true);
      const res = await getParDocSupplier({
        pareid: criteria.pareid,
        compliancecode: criteria.compliancecode,
        targetstatus: criteria.targetstatus,
      });
      if (fetchId !== lastFetchId.current) return;
      if (res.code === '200') {
        const list = Array.isArray(res.list) ? (res.list as RowItem[]) : [];
        const sorted = list.slice().sort((a, b) => (b.pareid || 0) - (a.pareid || 0));
        setData(sorted);
      } else {
        const apiMsg = (res as any)?.error_description;
        notification.error({ message: apiMsg || (res as any)?.errordescription ||  res.message || 'Une erreur est survenue' });
        setData([]);
      }
    } catch (err: any) {
      notification.error({ message: err?.error_description || err?.errordescription || err?.message || 'Erreur lors du chargement' });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);


  const onSearch = (values: any) => {
    fetchData(values);
  };

  const addRow = () => {
    const row: RowItem = {
      pareid: Date.now(),
      compliancecode: '',
      targetstatus: '',
      targetreasoncode: '',
      targetreason: '',
      isNew: true,
    };
    setData((prev) => [row, ...prev]);
  };

  const saveRow = async (record: RowItem) => {
    try {
      const values = form.getFieldsValue();
      const payload: ParDocSupplierItem = {
        pareid: record.isNew ? 0 : record.pareid,
        compliancecode: values[`compliancecode_${record.pareid}`],
        targetstatus: values[`targetstatus_${record.pareid}`],
        targetreasoncode: values[`targetreasoncode_${record.pareid}`],
        targetreason: values[`targetreason_${record.pareid}`],
      } as any;

      const fieldNames = [
        `compliancecode_${record.pareid}`,
        `targetstatus_${record.pareid}`,
      ];
      try {
        await form.validateFields(fieldNames as any);
      } catch (err: any) {
        const labelMap: Record<string, string> = {
          compliancecode: 'Code conformité',
          targetstatus: 'Statut cible',
        };
        const errors = err?.errorFields as Array<{ name: any[]; errors: string[] }>;
        if (errors?.length) {
          const lines = errors.map((e) => {
            const raw = String(e.name?.[0] ?? '');
            const key = raw.split('_')[0];
            const friendly = labelMap[key] ?? key;
            return `- ${friendly}: ${e.errors.join(', ')}`;
          });
          notification.error({
            message: 'Champs obligatoires manquants',
            description: (
              <div>
                {lines.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            ),
          });
          form.scrollToField(errors[0].name as any, { block: 'center' });
        }
        return;
      }

      const res = record.isNew
        ? await addParDocSupplier(payload)
        : await updateParDocSupplier(record.pareid, payload);
      if (res.code === '200') {
        notification.success({ message: res.message });
        fetchData({});
      } else {
        const apiMsg = (res as any)?.error_description;
        notification.error({ message: apiMsg || (res as any)?.errordescription ||  res.message || 'Une erreur est survenue' });
      }
    } catch (err: any) {
      if (err?.errorFields) return; // form validation already displayed
      notification.error({ message: err?.error_description || err?.errordescription || err?.message || "Erreur lors de l'enregistrement" });
    }
  };

  const removeRow = async (record: RowItem) => {
    try {
      if (record.isNew) {
        setData((prev) => prev.filter((r) => r.pareid !== record.pareid));
        return;
      }
      const res = await removeParDocSupplier(record.pareid);
      if (res.code === '200') {
        notification.success({ message: res.message });
        fetchData({});
      } else {
        const apiMsg = (res as any)?.error_description;
        notification.error({ message: apiMsg || (res as any)?.errordescription|| res.message ||  'Une erreur est survenue' });
      }
    } catch (err: any) {
      notification.error({ message: err?.error_description || err?.errordescription || err?.message || 'Erreur lors de la suppression' });
    }
  };

  const columns: DataGridColDef<RowItem>[] = [
    { comcode: 'pareid', dataIndex: 'pareid', width: 90, render: (_, r) => r.isNew ? 'Nouveau' : r.pareid , filtrable:true},
    { comcode: 'compliancecode', dataIndex: 'compliancecode', filtrable: true, filterComponent: <ParListSelect parcode="COMPLIANCE" allowClear/>,width: 200, render: (_, r) => (
      <FormInput noStyle name={`compliancecode_${r.pareid}`} initialValue={r.compliancecode} rules={[{ required: true, message: 'Compliance est obligatoire' }] }>
        <ParListSelect parcode="COMPLIANCE" allowClear />
      </FormInput>
    )},
    { comcode: 'targetstatus', dataIndex: 'targetstatus', filtrable: true, filterComponent:<ParListSelect parcode="DOCEFACT" allowClear />,width: 200, render: (_, r) => (
      <FormInput noStyle name={`targetstatus_${r.pareid}`} initialValue={r.targetstatus} rules={[{ required: true, message: 'Statut cible est obligatoire' }] }>
        <ParListSelect parcode="DOCEFACT" allowClear />
      </FormInput>
    )},
    { comcode: 'targetreasoncode', dataIndex: 'targetreasoncode', width: 220, render: (_, r) => (
      <FormInput noStyle name={`targetreasoncode_${r.pareid}`} initialValue={r.targetreasoncode}>
        <ParListSelect parcode="EFACTREAS" allowClear />
      </FormInput>
    )},
    { comcode: 'targetreason', dataIndex: 'targetreason', width: 260, render: (_, r) => (
      <FormInput noStyle name={`targetreason_${r.pareid}`} initialValue={r.targetreason}>
        <ParListSelect parcode="EFACTSREAS" allowClear/>
      </FormInput>
    )},
    {
      comcode: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <SaveButton comcode="save" onClick={(e) => { e.preventDefault(); saveRow(record); }} onlyIcon />
          <Popconfirm title="Delete?" onConfirm={() => removeRow(record)}>
            <DeleteButton onlyIcon />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <DF.Provider pagcode="pardocefact">
      <Form form={form}>
        <DataGrid
          rowKey="pareid"
          titleComcode="pardocefact"
          dataSource={data}
          columns={columns}
          isLoading={loading}
          onSearch={onSearch}
          initialSearchValues={{}}  
          extractData="pardocefact"
          extraActions={
            <NewButton comcode="Ajouter" type="primary" icon={<PlusOutlined />} onClick={addRow} style={{ height: 50, padding: '0 16px', minWidth: 200 }} />
          }
        />
      </Form>
    </DF.Provider>
  );
};

export default ParDocSuppInvoice;

